import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Stack } from "@mui/material";
import { $getSelection, $isRangeSelection } from "lexical";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { $isInDataTableNode, findDataTableNode } from "../../../utils/is-in-table-node";
import { DataTableBackgroundColour } from "./data-table-background-colour";
import { DataTableBorder } from "./data-table-border";
import { DataTableDelete } from "./data-table-delete";
import { DataTableMenuInsert } from "./data-table-menu-insert";
import { DataTablePadding } from "./data-table-padding";

import type { DataTableNode } from "../../../nodes/data-table-node";
import type { FC } from "react";

export const DataTableMenuPlugin: FC<{ anchorElem: HTMLElement }> = ({ anchorElem }) => {
  const [editor] = useLexicalComposerContext();
  const [tableNode, setTableNode] = useState<DataTableNode | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);

  useEffect(
    () =>
      editor.registerUpdateListener(() => {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = selection.anchor.getNode();
            setTableNode($isInDataTableNode(node) ? findDataTableNode(node) : null);
          } else {
            setTableNode(null);
          }
        });
      }),
    [editor],
  );

  useLayoutEffect(() => {
    if (!tableNode) {
      return;
    }

    const updatePosition = (): void => {
      const tableElement = editor.getElementByKey(tableNode.getKey());
      if (!tableElement) {
        return;
      }

      const anchorRect = anchorElem.getBoundingClientRect();
      const tableRect = tableElement.getBoundingClientRect();
      const menuHeight = menuButtonRef.current?.offsetHeight || 50;

      setMenuPosition({
        top: tableRect.top - anchorRect.top - menuHeight - 8,
        left: tableRect.left - anchorRect.left + tableRect.width / 2,
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [tableNode, editor, anchorElem]);

  if (!anchorElem || !(anchorElem instanceof HTMLElement)) {
    return null;
  }

  return createPortal(
    <>
      {tableNode && (
        <div
          className="data-table-menu-button"
          ref={menuButtonRef}
          style={{
            position: "absolute",
            top: menuPosition ? `${menuPosition.top}px` : "-1000px",
            left: menuPosition ? `${menuPosition.left}px` : "0px",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          <Stack
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 1,
              boxShadow: 3,
              p: 0,
            }}
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            width="100%"
          >
            <DataTableBackgroundColour
              nodeColour={tableNode.getBackgroundColor()}
              nodeKey={tableNode.getKey()}
            />
            <DataTableBorder nodeKey={tableNode.getKey()} border={tableNode.getBorder()} />
            <DataTablePadding nodeKey={tableNode.getKey()} padding={tableNode.getCellPadding()} />
            <DataTableMenuInsert />
            <DataTableDelete tableNode={tableNode} />
          </Stack>
        </div>
      )}
    </>,
    anchorElem,
  );
};
